import { createRoute, defer } from '@tanstack/react-router'
import { z } from 'zod'

import { socketTokenQueryOptions } from '@/features/feed'
import { createErrorFallback } from '@/fragments/create-error-fallback'
import { rootRoute } from '@/routes/root'
import { ensureAuthenticatedUser } from '@/utils/auth-wall'

import { reportQueryOptions, reportsQueryOptions } from './api/reports.service'
import { reportTypes } from './domain/report'
import { ReportCreateLayout } from './reportCreateLayout'
import { ReportEditLayout } from './reportEditLayout'
import { ReportsErrorComponent, ReportsLayout } from './reportsLayout'
import { ReportsSkeleton } from './reportsSkeleton'
import { ReportWebsocketRoute } from './reportWebsocketRoute'

const reportCreateSearchSchema = z.object({
  type: z.enum(reportTypes).catch('container'),
})

export const reportCreateRoute = createRoute({
  getParentRoute: () => reportCreateLayoutRoute,
  path: '/reports/create',
  validateSearch: reportCreateSearchSchema,
}).lazy(() => import('./reportCreate').then((d) => d.Route))

export const reportEditRoute = createRoute({
  getParentRoute: () => reportEditLayoutRoute,
  path: '/reports/edit/$reportID',
  errorComponent: createErrorFallback('An error occurred while loading report'),
  loader: async ({ context, params: { reportID } }) =>
    context?.queryClient?.ensureQueryData(reportQueryOptions(reportID)),
}).lazy(() => import('./reportEdit').then((d) => d.Route))

export const reportsRoute = createRoute({
  getParentRoute: () => reportsLayoutRoute,
  path: '/reports',
  errorComponent: createErrorFallback('An error occurred while loading reports'),
  loader: async ({ context }) => {
    if (!context.queryClient) return
    const customerID = context.customer?._id
    defer(
      context.queryClient.ensureQueryData(
        socketTokenQueryOptions(
          {
            type: 'customer',
            referenceId: customerID ?? '',
          },
          !!customerID
        )
      )
    )
    await context.queryClient.ensureQueryData(reportsQueryOptions())
  },
  pendingComponent: ReportsSkeleton,
}).lazy(() => import('./reportsList').then((d) => d.Route))

export const reportsLayoutRoute = createRoute({
  getParentRoute: () => reportWebsocketRoute,
  id: 'reports-layout',
  component: ReportsLayout,
  errorComponent: ReportsErrorComponent,
  beforeLoad: ensureAuthenticatedUser,
})

export const reportEditLayoutRoute = createRoute({
  getParentRoute: () => reportWebsocketRoute,
  id: 'report-edit-layout',
  component: ReportEditLayout,
  beforeLoad: ensureAuthenticatedUser,
})

export const reportCreateLayoutRoute = createRoute({
  getParentRoute: () => reportWebsocketRoute,
  id: 'report-create-layout',
  component: ReportCreateLayout,
  beforeLoad: ensureAuthenticatedUser,
})

export const reportWebsocketRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'report-websocket',
  component: ReportWebsocketRoute,
  beforeLoad: ensureAuthenticatedUser,
})
